/**
 * usePackagingOptions Hook
 *
 * Fetches packaging options for a list of raw product IDs.
 * Reusable across transfers, inventories, etc.
 * @see Sprint 22 - Transferts par packaging
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { InventoryPackagingOption, ApiSuccess } from '@bstock/shared';

/**
 * Fetches packaging options grouped by raw product ID
 */
async function fetchPackagingOptions(
  rawProductIds: string[]
): Promise<Record<string, InventoryPackagingOption[]>> {
  const response = await apiClient.get<
    ApiSuccess<Record<string, InventoryPackagingOption[]>>
  >('/supplier-catalog/packaging-options', {
    params: { rawProductIds: rawProductIds.join(',') },
  });
  return response.data.data;
}

/**
 * Hook to fetch packaging options for a list of raw products.
 * Returns a Record<rawProductId, InventoryPackagingOption[]>.
 * Only fetches when rawProductIds is non-empty.
 */
export function usePackagingOptions(rawProductIds: string[]) {
  return useQuery({
    queryKey: ['packaging-options', ...rawProductIds.sort()],
    queryFn: () => fetchPackagingOptions(rawProductIds),
    enabled: rawProductIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
